import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

import { SessionContext } from '@/shared/session';

import type { PermissionCode, PermissionContextValue } from './permissionTypes';

/**
 * Authorization / permission context.
 *
 * Provides `hasPermission(code)` checks for role-based UI visibility. The granted
 * codes come from the active session (`GET /api/v1/me` → tenant-scoped permissions,
 * mapped by the BFF from Keycloak roles). They can be overridden via the
 * `permissions` prop (tests / Storybook); when neither a prop nor a session is
 * present a permissive mock list is used so isolated renders still work.
 */

const DEFAULT_PERMISSIONS: PermissionCode[] = [
  'settings.*',
  'employees.manage',
  'roles.manage',
  'products.view',
  'catalog.view',
  'catalog.manage',
  'catalog.pricing.view',
  'catalog.pricing.manage',
  'content.view',
  'content.manage',
  'loyalty.view',
  'loyalty.manage',
];

/** Match a granted code against a requested code, honouring wildcards. */
function matchesPermission(granted: PermissionCode, requested: PermissionCode): boolean {
  if (granted === '*') {
    return true;
  }
  if (granted.endsWith('.*')) {
    const prefix = granted.slice(0, -1); // keep trailing dot, e.g. "settings."
    return requested.startsWith(prefix);
  }
  return granted === requested;
}

// Context + provider intentionally co-located (see required project structure);
// the hook lives in usePermissions.ts.
// eslint-disable-next-line react-refresh/only-export-components
export const PermissionContext = createContext<PermissionContextValue | undefined>(undefined);

interface PermissionProviderProps {
  children: ReactNode;
  /** Override the granted permissions (useful for tests / future wiring). */
  permissions?: PermissionCode[];
}

export function PermissionProvider({ children, permissions }: PermissionProviderProps) {
  const session = useContext(SessionContext);
  const granted = permissions ?? session?.permissions ?? DEFAULT_PERMISSIONS;

  const value = useMemo<PermissionContextValue>(() => {
    const hasPermission = (permission: PermissionCode): boolean =>
      granted.some((code) => matchesPermission(code, permission));

    return {
      permissions: granted,
      hasPermission,
      hasAnyPermission: (requested) => requested.some(hasPermission),
      hasAllPermissions: (requested) => requested.every(hasPermission),
    };
  }, [granted]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}
